// Angular modules.
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CalendarUtilsService } from 'app/calendar/services/calendar-utils.service';
// Zenklub enums.
import { ESidebarMode } from 'app/enums/sidebarmode.enum';
// Zenklub interfaces.
import { IBusySlot } from 'app/interfaces/availability.interface';
import {
	ICalendarEvent,
	IEvent,
	isIEvent,
} from 'app/interfaces/event.interface';
import {
	AppointmentSidebarFrom,
	ISidebar,
} from 'app/interfaces/sidebar.interface';
import { AvailabilitiesService } from 'app/services/availabilities.service';
// Zenklub services.
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { enterAnimation } from 'app/utils/animations.util';
import { PromptuarySidebarService } from 'modules/promptuary/services/sidebar/promptuary-sidebar.service';
import { MessageService } from 'primeng/api';
// Third-party modules.
import { Subscription, forkJoin } from 'rxjs';

@Component({
	selector: 'appointment-preview-list',
	templateUrl: './appointment-preview-list.component.html',
	styleUrls: ['./appointment-preview-list.component.scss'],
	providers: [MessageService],
	animations: [enterAnimation],
})
export class AppointmentPreviewListComponent implements OnChanges, OnInit {
	// Inputs.
	@Input() activeSidebar: ISidebar;
	@Input() selectedMode?: string;
	@Input() forceRefreshEvents?: boolean;
	// Outputs.
	@Output() showEvent: EventEmitter<ISidebar> = new EventEmitter<ISidebar>();
	@Output() showTodayEvents: EventEmitter<ISidebar> =
		new EventEmitter<ISidebar>();
	@Output() changeHeight: EventEmitter<any> = new EventEmitter<any>();
	// Conditionals
	showLastEvents = false;
	// Events.
	eventsForToday: (IBusySlot | ICalendarEvent)[]; //
	eventsForWeekWithoutPromptuary: ICalendarEvent[] = [];
	// Labels.
	screenDate: string;
	// Status.
	isLoading = false;
	isTodayVisible = false;
	isStarted = false;
	userRequestedReschedule = false;
	// Listener for new events.
	newEvSubscription: Subscription;
	// Constructor method.
	constructor(
		private dateTime: DateTimeService,
		private events: EventsService,
		private translate: TranslateService,
		private calendarUtils: CalendarUtilsService,
		private availabilitiesService: AvailabilitiesService,
		private promptuarySidebarService: PromptuarySidebarService
	) {}

	// On init.
	ngOnInit() {
		this.isLoading = true;

		// Subscription for new Events.
		this.events
			.eventEmitter()
			.subscribe((event) => this.appendItemToList(event, true));
		this.promptuarySidebarService.registerObservable.subscribe(
			(registerAppointment) => {
				if (registerAppointment) {
					this.eventsForWeekWithoutPromptuary =
						this.eventsForWeekWithoutPromptuary.filter((appointment) => {
							return registerAppointment.id !== appointment.id;
						});
				}
			}
		);
	}

	// On changes.
	ngOnChanges() {
		// (Re)start section.
		this.startList();
	}

	// Start section.
	private startList(showLoader = true): void {
		// Is today?
		this.isTodayVisible = this.showingToday();
		// Get events for current day.
		const dt: Date = this.activeSidebar.date || new Date();
		this.showDayEvents(dt, showLoader);
		// Set status.
		this.isStarted = true;
	}

	// Show events of a specific day.
	private showDayEvents(date: Date, showLoader = true): void {
		// Loading.
		this.isLoading = showLoader;
		// Reference to this.
		this.eventsForToday = [];

		forkJoin([
			this.events.getEventsForDay(date),
			this.events.getEventsWithoutPromptuary(new Date()),
		]).subscribe({
			next: ([eventsForDay, eventsWithoutPromptuary]) => {
				this.eventsForWeekWithoutPromptuary = eventsWithoutPromptuary.filter(
					(event) => event.hasPromptuary === false && event.noShow === false
				);

				const todEvents: any[] = eventsForDay;
				todEvents.forEach((item) => {
					// Append item to events list.
					this.appendItemToList(item);
				});
				this.sortEvents();
				// Set screen date.
				this.screenDate = this.dateTime.getInFullDate(date, true);
				// Loaded.
				this.isLoading = false;
				// Change calendar height.
				this.changeHeight.emit();
				// Scroll if needed.
				this.goToSidebar();
			},
		});

		this.availabilitiesService.getBusySlotsForDay(date).then((result) => {
			const todEvents: any[] = result;
			todEvents.forEach((item) => {
				this.eventsForToday.push(item);
			});
			this.sortEvents();
		});
	}

	public sortEvents(): void {
		this.eventsForToday.sort((a, b): number => {
			let fromDate: any;
			let toDate: any;

			if (isIEvent(a)) {
				fromDate = (a as ICalendarEvent).day;
			} else {
				fromDate = (a as IBusySlot).startDate;
			}
			if (isIEvent(b)) {
				toDate = (b as ICalendarEvent).day;
			} else {
				toDate = (b as IBusySlot).startDate;
			}
			return fromDate.getTime() - toDate.getTime();
		});
	}

	// Append an item to events list.
	public appendItemToList(item: ICalendarEvent, update = false): void {
		if (item) {
			if (item.status === 'cancelled' && !update) {
				return;
			}
			const idx = this.eventsForToday.findIndex((e) => item.id === e.id);
			if (idx !== -1) {
				if (item.status === 'cancelled') {
					this.eventsForToday.splice(idx, 1);
				} else {
					// just update the event;
					this.eventsForToday[idx] = item;
					if (update) {
						this.startList(false);
					}
				}
			} else {
				this.eventsForToday.push(item);
			}
		}
	}

	// Verify if today is visible.
	private showingToday(): boolean {
		const today: Date = new Date();
		const activeDate: Date = this.activeSidebar.date;
		today.setHours(0, 0, 0, 0);
		activeDate.setHours(0, 0, 0, 0);
		return today.toString() === activeDate.toString();
	}

	// Show event details.
	public showEventDetails = (
		event: IEvent,
		type?: 'event' | 'busyslot'
	): void => {
		if (!type && !event.status) {
			type = 'busyslot';
		} else if (!type) {
			type = null;
		}

		this.calendarUtils.openDetailsModal(
			event.id,
			type,
			this.showEvent,
			event as ICalendarEvent
		);
	};

	// Add new event.
	public addEvent(): void {
		// Update sidebar info.
		this.activeSidebar.id = null;
		this.activeSidebar.mode = ESidebarMode.New;
		this.activeSidebar.from = AppointmentSidebarFrom.HOME_SIDEBAR;
		// Show details.
		this.showEvent.emit(this.activeSidebar);
	}

	// Show today events.
	backToToday(): void {
		const sidebar: ISidebar = {
			date: new Date(),
			id: null,
			mode: ESidebarMode.Appointments,
		};
		this.showTodayEvents.emit(sidebar);
	}

	// Scrolls to details on mobile.
	goToSidebar(): void {}

	showLastWeekAppointmentsWithoutTags() {
		this.showLastEvents = !this.showLastEvents;
	}
}
